import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useClickOutside } from '../../hooks';
import LogoutConfirmationModal from '../common/LogoutConfirmationModal';
import { ASSET_PATHS } from '../../constants';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  
  // Close dropdown when clicking outside
  const profileMenuRef = useClickOutside<HTMLDivElement>(() => {
    setShowProfileMenu(false);
  });

  // Close mobile menu when clicking outside
  const mobileMenuRef = useClickOutside<HTMLDivElement>(() => {
    setShowMobileMenu(false);
  });

  // Get profile image URL - ensure it's a full URL
  const getProfileImageUrl = useCallback((): string | null => {
    if (!user) {
      console.log('[Header] No user object');
      return null;
    }
    
    // Check multiple possible field names for profile image
    const profileImage = user.profile_image || (user as any).image || (user as any).avatar || (user as any).photo;
    
    if (!profileImage || typeof profileImage !== 'string' || profileImage.trim() === '') {
      console.log('[Header] No profile image found. User object:', user);
      return null;
    }
    
    const img = profileImage.trim();
    console.log('[Header] Raw profile image value:', img);
    
    // If already a full URL, return as is
    if (img.startsWith('http://') || img.startsWith('https://')) {
      console.log('[Header] Profile image is already a full URL:', img);
      return img;
    }
    
    // If relative path, prepend S3 base URL
    const fullUrl = `https://lechalo.s3.ap-south-1.amazonaws.com${img.startsWith('/') ? img : '/' + img}`;
    console.log('[Header] Constructed profile image URL:', fullUrl);
    return fullUrl;
  }, [user]);

  // Reset image error and loading state when user or profile image changes
  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
    
    // Debug: Log profile image info
    if (user) {
      console.log('[Header] User object:', user);
      console.log('[Header] User profile_image:', user.profile_image);
      const imgUrl = getProfileImageUrl();
      console.log('[Header] Final profile image URL:', imgUrl);
    } else {
      console.log('[Header] No user object available');
    }
  }, [user?.profile_image, user?.id, getProfileImageUrl, user]);

  const navigationItems = [
    { name: 'Home', path: '/home', icon: 'home' },
    { name: 'History', path: '/history', icon: 'clock', requiresAuth: true },
    { name: 'Support', path: '/support', icon: 'help' },
  ].filter(item => !item.requiresAuth || isAuthenticated);

  const isActive = (path: string) => {
    return location.pathname.includes(path.split('/').pop() || '');
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowProfileMenu(false);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileMenuToggle = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    setShowProfileMenu(false);
  };

  const handleNavigationClick = (path: string) => {
    // Close mobile menu when navigating
    setShowMobileMenu(false);
    
    // Check if the path requires authentication (History only - Support is now public)
    const requiresAuth = path === '/history';
    
    if (requiresAuth && !isAuthenticated) {
      // Redirect to login page if not authenticated
      navigate('/login');
    } else {
      // Navigate normally if authenticated or path doesn't require auth
      navigate(path);
    }
  };

  const handleMobileMenuToggle = () => {
    setShowMobileMenu(!showMobileMenu);
    setShowProfileMenu(false);
  };

  // Get user initials from first and last name
  const getUserInitials = (): string => {
    if (!user) return 'U';
    const firstLetter = user.f_name ? user.f_name.charAt(0).toUpperCase() : '';
    const lastLetter = user.l_name ? user.l_name.charAt(0).toUpperCase() : '';
    
    if (firstLetter && lastLetter) {
      return firstLetter + lastLetter;
    }
    
    // Fallback to name if f_name/l_name not available
    if (user.name) {
      const names = user.name.trim().split(' ');
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      }
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    
    return 'U';
  };

  const userInitials = getUserInitials();
  
  const profileImageUrl = getProfileImageUrl();
  const hasProfileImage = profileImageUrl !== null;
  
  // Get user's first name for profile button
  const getUserFirstName = (): string => {
    if (!user) return 'Profile';
    if (user.f_name) return user.f_name;
    if (user.name) {
      const names = user.name.trim().split(' ');
      return names[0];
    }
    return 'Profile';
  };

  const userFirstName = getUserFirstName();

  return (
    <>
      <header className="website-header">
        <div className="header-container">
          {/* Logo Section */}
          <div className="logo-section" onClick={() => navigate('/home')}>
            <img 
              src={ASSET_PATHS.LOGO} 
              alt="LeChalo Logo" 
              className="logo-image"
            />
            <div className="logo-text">Safar Ka Hamsafar</div>
          </div>

          {/* Navigation */}
          <div className="navigation">
            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-button"
              onClick={handleMobileMenuToggle}
              aria-label="Toggle menu"
              aria-expanded={showMobileMenu}
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Navigation Links */}
            <nav className="nav-links">
              {navigationItems.map((item) => (
                <button
                  key={item.path}
                  className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => handleNavigationClick(item.path)}
                >
                  {item.name}
                </button>
              ))}
            </nav>

            {/* Profile Section */}
            {isAuthenticated ? (
              <div className="profile-section" ref={profileMenuRef}>
                <button
                  className="profile-button"
                  onClick={handleProfileMenuToggle}
                  aria-expanded={showProfileMenu}
                  aria-haspopup="true"
                >
                  <div className={`profile-avatar ${hasProfileImage && !imageError && profileImageUrl ? 'has-image' : ''}`}>
                    {hasProfileImage && !imageError && profileImageUrl ? (
                      <img 
                        src={profileImageUrl} 
                        alt={userFirstName || 'Profile'} 
                        className="profile-avatar-image"
                        onLoad={() => {
                          console.log('[Header] ✅ Profile image loaded successfully:', profileImageUrl);
                          setImageLoading(false);
                        }}
                        onError={(e) => {
                          console.error('[Header] ❌ Failed to load profile image');
                          console.error('[Header] Image URL:', profileImageUrl);
                          console.error('[Header] Error details:', e);
                          setImageError(true);
                          setImageLoading(false);
                        }}
                      />
                    ) : (
                      <span className="profile-initials">{userInitials}</span>
                    )}
                  </div>
                  <span className="profile-name">
                    {userFirstName}
                  </span>
                  <svg 
                    className={`dropdown-icon ${showProfileMenu ? 'rotated' : ''}`} 
                    width="16" 
                    height="16" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                <div className={`profile-menu ${showProfileMenu ? 'show' : ''}`}>
                    <button 
                      className="menu-item" 
                      onClick={() => handleMenuClick('/profile')}
                    >
                      My Profile
                    </button>
                    <button 
                      className="menu-item" 
                      onClick={() => handleMenuClick('/payments/history')}
                    >
                      My Comfy Shuttle Booking
                    </button>
                    {/* <button 
                      className="menu-item" 
                      onClick={() => handleMenuClick('/features/notifications')}
                    >
                      Notifications
                    </button> */}
                    <button 
                      className="menu-item" 
                      onClick={() => handleMenuClick('/legal/policies')}
                    >
                      Legal & Policies
                    </button>
                    <button 
                      className="menu-item" 
                      onClick={() => handleMenuClick('/support')}
                    >
                      Help
                    </button>
                    <button 
                      className="menu-item logout" 
                      onClick={handleLogoutClick}
                    >
                      Logout
                    </button>
                  </div>
              </div>
            ) : (
              <div className="profile-section">
                <button
                  className="login-button"
                  onClick={() => navigate('/login')}
                >
                  Login
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`mobile-menu ${showMobileMenu ? 'show' : ''}`}
          ref={mobileMenuRef}
        >
          <nav className="mobile-nav-links">
            {navigationItems.map((item) => (
              <button
                key={item.path}
                className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavigationClick(item.path)}
                style={{ display: 'flex' }}
              >
                {item.name}
              </button>
            ))}
            {!isAuthenticated && (
              <button
                className="mobile-nav-link mobile-login-button"
                onClick={() => {
                  setShowMobileMenu(false);
                  navigate('/login');
                }}
                style={{ display: 'flex' }}
              >
                Login
              </button>
            )}
          </nav>
        </div>
      </header>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
};

export default Header;
